import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DIRECTION, SCREEN_SIZES } from 'constants/config';
import {
  Drag,
  ArrowUp,
  ArrowDown,
  StatusCheck,
  StatusClose,
} from 'components/ui/Icon/Streamline';
import { TEXT_VARIANTS, Text } from 'components/ui/Typography';
import Card from 'components/ui/Card';
import Tooltip from 'components/shared/Tooltip';
import useWindowSize from 'hooks/useWindowSize';

import { ItemTypes } from 'constants/reactDnDItemTypes';
import useSmoothScroll from 'hooks/useSmoothScroll';
import { CustomDragLayer } from './CustomDragLayer';

const SortingChoice = props => {
  const { onDragStart, onDragEnd } = useSmoothScroll();
  const {
    index,
    text,
    disabledOption = { up: false, down: false },
    onReorder,
    moveChoice,
    correct,
    a11yActive,
    handleA11y,
    questionId,
  } = props;

  const isReadOnly = correct !== undefined;
  const ref = useRef(null);
  const size = useWindowSize();
  const isMobile = size.width < SCREEN_SIZES.MD;

  const [{ handlerId }, drop] = useDrop({
    accept: ItemTypes.CHOICE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveChoice(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // props.order(index, hoverIndex);
      monitor.getItem().index = hoverIndex;
      // item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.CHOICE,
    item: () => {
      onDragStart();
      return { index, ...props };
    },
    end: () => {
      onDragEnd();
    },
    canDrag: () => !isReadOnly,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const renderIcon = () => {
    if (correct) {
      return <StatusCheck className="fs-0" />;
    }
    return <StatusClose className="fs-0" />;
  };

  useEffect(() => {
    // Uses a transparent empty Image to hide the browser-drawn drag preview completely
    preview(getEmptyImage());
  }, [preview]);

  const opacity = isDragging ? 0 : 1;

  drag(drop(ref));

  return (
    <>
      <button
        ref={ref}
        disabled={isReadOnly}
        style={{ opacity }}
        data-handler-id={handlerId}
        type="button"
        onClick={e => e.stopPropagation()}
        aria-describedby={`sortingDescription-${questionId}`}
        id={`SortingChoice-index-${index}`}
        onKeyDown={e => {
          if (e.key === ' ') {
            handleA11y();
          }
          if ((e.key === 'Tab' || e.key === 'Escape') && a11yActive) {
            handleA11y();
          }
          if (e.key === 'ArrowUp' && !disabledOption.up && a11yActive) {
            e.preventDefault();
            onReorder(DIRECTION.UP);
          }
          if (e.key === 'ArrowDown' && !disabledOption.down && a11yActive) {
            e.preventDefault();
            onReorder(DIRECTION.DOWN);
          }
        }}
        className={classNames(
          `${props?.className} u-resetBtn w-100p SortingChoice`,
          {
            'h-cr-d': isReadOnly,
          },
        )}
      >
        <Card
          className={classNames('d-f fd-r ai-c jc-sb px-14', {
            'h-cr-g': !isReadOnly,
            'py-10': !isReadOnly && !isMobile,
            'py-16': isReadOnly,
            'py-20': isMobile,
          })}
          mantleOverride
          noHover={isReadOnly}
        >
          <div className="d-f fd-r ai-c ">
            {!isReadOnly && <Drag className="fs-0" />}
            <Text className="ml-8" variant={TEXT_VARIANTS.MANTLE_BODY}>
              {text}
            </Text>
          </div>
          {!isReadOnly ? (
            <div className="d-f ai-c SortingChoice-actionButtons">
              <div
                className="IconButton h-cr-p SortingChoice-button"
                type="button"
                aria-label={`${t('common.move_up')} ${index}`}
                title={`${t('common.move_up')} ${index}`}
                onClick={() => {
                  if (!disabledOption.up) {
                    onReorder(DIRECTION.UP);
                  }
                }}
                onKeyDown={e => {
                  if (
                    (e.key === ' ' || e.key === 'Enter') &&
                    !disabledOption.up
                  ) {
                    onReorder(DIRECTION.UP);
                  }
                }}
              >
                <Tooltip
                  disabled={disabledOption.up}
                  text={t('common.move_up')}
                  className="fz-14"
                >
                  <ArrowUp
                    className={classNames('SortingChoice-arrowIcon fs-0', {
                      'SortingChoice-arrowIcon-disabled': disabledOption.up,
                    })}
                    size={isMobile ? 20 : 16}
                  />
                </Tooltip>
              </div>
              <div
                className="IconButton h-cr-p SortingChoice-button"
                type="button"
                aria-label={`${t('common.move_down')} ${index}`}
                title={`${t('common.move_down')} ${index}`}
                onClick={() => {
                  if (!disabledOption.down) {
                    onReorder(DIRECTION.DOWN);
                  }
                }}
                onKeyDown={e => {
                  if (
                    (e.key === ' ' || e.key === 'Enter') &&
                    !disabledOption.down
                  ) {
                    onReorder(DIRECTION.DOWN);
                  }
                }}
              >
                <Tooltip
                  disabled={disabledOption.down}
                  text={t('common.move_down')}
                  className="fz-14"
                >
                  <ArrowDown
                    className={classNames('SortingChoice-arrowIcon fs-0', {
                      'SortingChoice-arrowIcon-disabled': disabledOption.down,
                    })}
                    size={isMobile ? 20 : 16}
                  />
                </Tooltip>
              </div>
            </div>
          ) : (
            renderIcon()
          )}
        </Card>
      </button>
      <CustomDragLayer />
    </>
  );
};

export default SortingChoice;
